import { Box, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { NUMERIC_INPUT_WIDTH } from "../../components/staffing/ScheduleExport/consts";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InfoIconWithTooltip } from "../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { MAX_APPEND_EMPTY_COLUMNS } from "../../downloads/dailyPrintout/constants";

const MIN_APPEND_EMPTY_COLUMNS = 0;

/** A typed whole number, optionally negative — the only input that commits. "2.5" and "" never do. */
const WHOLE_NUMBER_PATTERN = /^-?\d+$/;

/** Typed text as the count it commits: whole numbers clamped into range, anything else `null`. */
const toEmptyColumnCount = (raw: string): number | null => {
	if (!WHOLE_NUMBER_PATTERN.test(raw.trim())) return null;
	return Math.min(MAX_APPEND_EMPTY_COLUMNS, Math.max(MIN_APPEND_EMPTY_COLUMNS, Number.parseInt(raw, 10)));
};

const isCommittableCount = (raw: string): boolean => {
	const count = toEmptyColumnCount(raw);
	return count !== null && String(count) === raw.trim();
};

interface AppendEmptyColumnsSettingProps {
	isDetailed: boolean;
	appendEmptyColumns: number;
	onAppendEmptyColumnsChange: (count: number) => void;
}

/**
 * Detailed-only count of blank bordered columns added after each staff group's last column.
 *
 * A typed whole number commits at once, clamped into 0–MAX (so "15" shows and prints 10, "-2" shows and
 * prints 0). Text that is not a whole number ("", "2.5") is kept on screen with the invalid message and
 * commits nothing; leaving the field puts the last committed count back.
 */
export const AppendEmptyColumnsSetting: FC<AppendEmptyColumnsSettingProps> = ({ isDetailed, appendEmptyColumns, onAppendEmptyColumnsChange }) => {
	const { t } = useTranslation();
	// `null` while the field shows the committed count; the raw text only while it isn't a whole number.
	const [invalidDraft, setInvalidDraft] = useState<string | null>(null);
	if (!isDetailed) return null;

	const label = t("printout.append.empty.columns");
	const handleChange = (raw: string) => {
		const count = toEmptyColumnCount(raw);
		if (count === null) {
			setInvalidDraft(raw);
			return;
		}
		setInvalidDraft(null);
		onAppendEmptyColumnsChange(count);
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Typography variant="body2" sx={{ color: "primary.dark", fontWeight: 500 }}>
					{label}
				</Typography>
				<InfoIconWithTooltip dataTestId="printout-append-empty-columns-info" title={t("printout.append.empty.columns.description")} />
			</Box>
			<SystemValidationTextField
				value={invalidDraft ?? String(appendEmptyColumns)}
				onChange={(event) => handleChange(event.target.value)}
				inputType="number"
				reserveHeightForHelperText={false}
				// The field is page-count narrow like its numeric neighbours; the form control stays full width so
				// the invalid message underneath reads on one line instead of wrapping inside the input's width.
				formControlSx={{ width: "100%" }}
				inputSx={{ "& .MuiInputBase-root": { width: NUMERIC_INPUT_WIDTH } }}
				dataTestId="printout-append-empty-columns-input"
				injectedProps={{
					onBlur: () => setInvalidDraft(null),
					slotProps: {
						htmlInput: { "aria-label": label, min: MIN_APPEND_EMPTY_COLUMNS, max: MAX_APPEND_EMPTY_COLUMNS, step: 1, inputMode: "numeric" },
					},
				}}
				validation={{
					validationCallback: isCommittableCount,
					helperText: { invalid: t("printout.append.empty.columns.invalid", { max: MAX_APPEND_EMPTY_COLUMNS }) },
					performValidation: "onlyAfterInitialChange",
					showValidWhenValid: "never",
				}}
			/>
		</Box>
	);
};
